import { useState, useEffect, useCallback } from 'react';
import type { FeeDefaulterRecord, FeeDefaulterListMeta } from '../../services/feeDefaultersApi';
import { fetchFeeDefaulters } from '../../services/feeDefaultersApi';
import { fetchClasses, fetchSections } from '../../services/studentApi';
import type { ClassOption, SectionOption } from '../../services/studentApi';
import type { ApiError } from '../../services/api';
import { printFeeDefaulters } from '../../utils/printFeeDefaulters';
import type { FeeDefaulterPrintRow } from '../../utils/printFeeDefaulters';
import { useCurrentSchoolName } from '../../hooks/useCurrentSchoolName';
import RupeeIcon from '../icons/RupeeIcon';
import './FeeDefaulters.css';

function formatCurrency(n: number): string {
    return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

/* The fee-defaulters report is always scoped to one class (the API requires it), so a
   fetch this large per page is only ever pulling one class/section worth of rows. */
const PER_PAGE = 50;
/* The print job covers every defaulter matching the applied filters, not just the
   visible page, so it pulls the result set in large chunks. */
const PRINT_FETCH_SIZE = 100;

const STATUS_LABELS: Record<string, string> = {
    draft: 'Draft',
    published: 'Published',
    partial: 'Partial',
    paid: 'Paid',
    overdue: 'Overdue',
    void: 'Void',
};

function studentDisplayName(r: FeeDefaulterRecord): string {
    return [r.student.first_name, r.student.last_name].filter(Boolean).join(' ') || '—';
}

function classSectionLabel(r: FeeDefaulterRecord): string {
    return [r.class?.name, r.section?.name].filter(Boolean).join(' – ') || '—';
}

export default function FeeDefaulters() {
    const schoolName = useCurrentSchoolName();

    const [rows, setRows] = useState<FeeDefaulterRecord[]>([]);
    const [meta, setMeta] = useState<FeeDefaulterListMeta | null>(null);
    const [loading, setLoading] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [error, setError] = useState('');
    const [searched, setSearched] = useState(false);

    /* ── Draft filter inputs (not yet applied) ── */
    const [classInput, setClassInput] = useState('');
    const [sectionInput, setSectionInput] = useState('');

    /* ── Committed filter values (applied on Search) ── */
    const [appliedClassId, setAppliedClassId] = useState('');
    const [appliedSectionId, setAppliedSectionId] = useState('');

    /* ── Dropdown options ── */
    const [availableClasses, setAvailableClasses] = useState<ClassOption[]>([]);
    const [availableSections, setAvailableSections] = useState<SectionOption[]>([]);

    /* ── Overdue invoices drawer ── */
    const [drawerRow, setDrawerRow] = useState<FeeDefaulterRecord | null>(null);

    /* Load classes on mount */
    useEffect(() => {
        fetchClasses()
            .then((classes) => setAvailableClasses(classes))
            .catch(() => { /* silently fall back to empty */ });
    }, []);

    /* Cascade: reload sections when class selection changes */
    useEffect(() => {
        if (classInput) {
            fetchSections(classInput)
                .then((sections) => setAvailableSections(sections))
                .catch(() => setAvailableSections([]));
        } else {
            setAvailableSections([]);
        }
        setSectionInput('');
    }, [classInput]);

    /* class_id is mandatory on this endpoint, so there is nothing to load until a class
       has been applied — no fetch fires before the first search. */
    const loadData = useCallback(async (page: number, classId: string, sectionId: string) => {
        if (!classId) return;
        setLoading(true);
        setError('');
        try {
            const res = await fetchFeeDefaulters({
                class_id: classId,
                section_id: sectionId || undefined,
                per_page: PER_PAGE,
                page,
            });
            setRows(res.data);
            setMeta(res.meta);
            setDrawerRow(null);
        } catch (err: unknown) {
            const apiErr = err as ApiError;
            setError(apiErr.message || 'Failed to load fee defaulters.');
            setRows([]);
            setMeta(null);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleSearch = () => {
        if (!classInput) return;
        setAppliedClassId(classInput);
        setAppliedSectionId(sectionInput);
        setSearched(true);
        loadData(1, classInput, sectionInput);
    };

    const handleClear = () => {
        setClassInput('');
        setSectionInput('');
        setAppliedClassId('');
        setAppliedSectionId('');
        setSearched(false);
        setRows([]);
        setMeta(null);
        setError('');
    };

    const handlePageChange = (newPage: number) => {
        loadData(newPage, appliedClassId, appliedSectionId);
    };

    const appliedClassName = () => availableClasses.find((c) => c.id === appliedClassId)?.name;
    const appliedSectionName = () => availableSections.find((s) => s.id === appliedSectionId)?.name;

    /* ---- Print ---- */

    /** Every defaulter matching the applied filters, not just the visible page. */
    const fetchAllForPrint = async (): Promise<FeeDefaulterPrintRow[]> => {
        const out: FeeDefaulterPrintRow[] = [];
        let currentPage = 1;
        let lastPage = 1;
        do {
            const res = await fetchFeeDefaulters({
                class_id: appliedClassId,
                section_id: appliedSectionId || undefined,
                per_page: PRINT_FETCH_SIZE,
                page: currentPage,
            });
            for (const r of res.data) {
                out.push({
                    studentName: studentDisplayName(r),
                    rollNumber: r.roll_number,
                    classSection: classSectionLabel(r),
                    fatherName: r.father_name,
                    motherName: r.mother_name,
                    address: r.address,
                    contact: r.father_contact,
                    totalOverdueAmount: r.total_overdue_amount,
                    overdueInvoiceLabels: r.overdue_invoices.map(
                        (inv) => `${inv.invoice_number} — due ${formatDate(inv.due_date)} — ${formatCurrency(parseFloat(inv.balance_amount))}`
                    ),
                });
            }
            lastPage = res.meta.last_page || 1;
            currentPage += 1;
        } while (currentPage <= lastPage);
        return out;
    };

    const handlePrint = async () => {
        if (!appliedClassId) return;
        setPrinting(true);
        setError('');
        try {
            const printRows = await fetchAllForPrint();
            if (printRows.length === 0) {
                setError('No defaulters to print.');
                return;
            }
            printFeeDefaulters({
                schoolName: schoolName ?? undefined,
                className: appliedClassName() ?? 'Class',
                sectionName: appliedSectionName(),
                rows: printRows,
            });
        } catch (err: unknown) {
            const apiErr = err as ApiError;
            setError(apiErr.message || 'Failed to prepare print.');
        } finally {
            setPrinting(false);
        }
    };

    const from = meta ? (meta.current_page - 1) * meta.per_page + 1 : 0;
    const to = meta ? Math.min(meta.current_page * meta.per_page, meta.total) : 0;

    return (
        <div className="feedef">
            <div className="feedef__header">
                <div className="feedef__header-text">
                    <h1 className="feedef__title">Fee Defaulters</h1>
                    <p className="feedef__subtitle">Students with overdue fee invoices, by class.</p>
                </div>
            </div>

            {/* ── Filter Bar ── */}
            <div className="feedef__toolbar">
                <div className="feedef__toolbar-left">
                    <div className="feedef__filter-field">
                        <label className="feedef__filter-label" htmlFor="feedefClass">Class *</label>
                        <select
                            id="feedefClass"
                            className="feedef__filter-select"
                            value={classInput}
                            onChange={(e) => setClassInput(e.target.value)}
                        >
                            <option value="">Select class…</option>
                            {availableClasses.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="feedef__filter-field">
                        <label className="feedef__filter-label" htmlFor="feedefSection">Section</label>
                        <select
                            id="feedefSection"
                            className="feedef__filter-select"
                            value={sectionInput}
                            onChange={(e) => setSectionInput(e.target.value)}
                            disabled={!classInput}
                        >
                            <option value="">All sections</option>
                            {availableSections.map((s) => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="feedef__filter-actions">
                        <button className="feedef__search-btn" onClick={handleSearch} disabled={!classInput || loading}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                            Search
                        </button>
                        {searched && (
                            <button className="feedef__clear-btn" onClick={handleClear}>Clear</button>
                        )}
                    </div>
                </div>
                <div className="feedef__toolbar-right">
                    {meta && <span className="feedef__record-count">{meta.total} defaulter{meta.total !== 1 ? 's' : ''}</span>}
                    <button
                        className="feedef__print-btn"
                        onClick={handlePrint}
                        disabled={printing || loading || !appliedClassId || !meta || meta.total === 0}
                        title={appliedClassId ? 'Print defaulters list' : 'Select a class and search to print'}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 6 2 18 2 18 9" />
                            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                            <rect x="6" y="14" width="12" height="8" />
                        </svg>
                        {printing ? 'Preparing…' : 'Print List'}
                    </button>
                </div>
            </div>

            {/* ── Error ── */}
            {error && (
                <div className="feedef__error-banner">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
                    {error}
                </div>
            )}

            {/* ── Table ── */}
            <div className="feedef__table-wrap">
                {loading ? (
                    <div className="feedef__loading">
                        <div className="feedef__loader">
                            <span className="feedef__loader-dot" />
                            <span className="feedef__loader-dot" />
                            <span className="feedef__loader-dot" />
                        </div>
                    </div>
                ) : !searched ? (
                    <div className="feedef__empty">
                        <RupeeIcon size={36} strokeWidth={1.5} />
                        <h3 className="feedef__empty-title">Select a class to get started</h3>
                        <p className="feedef__empty-text">Choose a class (and optionally a section) above, then search.</p>
                    </div>
                ) : rows.length === 0 ? (
                    <div className="feedef__empty">
                        <RupeeIcon size={36} strokeWidth={1.5} />
                        <h3 className="feedef__empty-title">No fee defaulters found</h3>
                        <p className="feedef__empty-text">Every student in this class has cleared their fees.</p>
                    </div>
                ) : (
                    <table className="feedef__table">
                        <thead>
                            <tr>
                                <th className="feedef__th">Roll No.</th>
                                <th className="feedef__th">Student</th>
                                <th className="feedef__th">Father's Name</th>
                                <th className="feedef__th">Mother's Name</th>
                                <th className="feedef__th">Class</th>
                                <th className="feedef__th">Address</th>
                                <th className="feedef__th">Contact</th>
                                <th className="feedef__th feedef__th--center">Overdue Invoices</th>
                                <th className="feedef__th feedef__th--amount">Total Overdue</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, idx) => (
                                <tr
                                    className={`feedef__row ${idx % 2 === 1 ? 'feedef__row--even' : ''} ${drawerRow?.id === r.id ? 'feedef__row--active' : ''}`}
                                    key={r.id}
                                    onClick={() => setDrawerRow(r)}
                                >
                                    <td className="feedef__td feedef__td--roll">{r.roll_number || '—'}</td>
                                    <td className="feedef__td feedef__td--student">{studentDisplayName(r)}</td>
                                    <td className="feedef__td">{r.father_name || '—'}</td>
                                    <td className="feedef__td">{r.mother_name || '—'}</td>
                                    <td className="feedef__td">{classSectionLabel(r)}</td>
                                    <td className="feedef__td feedef__td--address">{r.address || '—'}</td>
                                    <td className="feedef__td feedef__td--phone">{r.father_contact || '—'}</td>
                                    <td className="feedef__td feedef__td--center">
                                        <span className="feedef__invoice-badge">{r.overdue_invoices.length}</span>
                                    </td>
                                    <td className="feedef__td feedef__td--amount">{formatCurrency(r.total_overdue_amount)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* ── Pagination ── */}
            {meta && meta.last_page > 1 && (
                <div className="feedef__pagination">
                    <span className="feedef__pagination-info">
                        Showing {from}–{to} of {meta.total}
                    </span>
                    <div className="feedef__pagination-controls">
                        <button
                            className="feedef__page-btn"
                            disabled={meta.current_page <= 1}
                            onClick={() => handlePageChange(1)}
                            title="First page"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="11 17 6 12 11 7" /><polyline points="18 17 13 12 18 7" /></svg>
                        </button>
                        <button
                            className="feedef__page-btn"
                            disabled={meta.current_page <= 1}
                            onClick={() => handlePageChange(meta.current_page - 1)}
                            title="Previous page"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
                        </button>
                        <span className="feedef__page-num">
                            Page {meta.current_page} of {meta.last_page}
                        </span>
                        <button
                            className="feedef__page-btn"
                            disabled={meta.current_page >= meta.last_page}
                            onClick={() => handlePageChange(meta.current_page + 1)}
                            title="Next page"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                        </button>
                        <button
                            className="feedef__page-btn"
                            disabled={meta.current_page >= meta.last_page}
                            onClick={() => handlePageChange(meta.last_page)}
                            title="Last page"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="13 17 18 12 13 7" /><polyline points="6 17 11 12 6 7" /></svg>
                        </button>
                    </div>
                </div>
            )}

            {/* ── Overdue Invoices Drawer ── */}
            {drawerRow && (
                <>
                    <div className="feedef__drawer-backdrop" onClick={() => setDrawerRow(null)} />
                    <aside className="feedef__drawer" role="dialog" aria-label={`Overdue invoices — ${studentDisplayName(drawerRow)}`}>
                        <header className="feedef__drawer-header">
                            <div className="feedef__drawer-header-left">
                                <button className="feedef__drawer-close" onClick={() => setDrawerRow(null)} aria-label="Close panel" title="Close">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                </button>
                                <div>
                                    <h2 className="feedef__drawer-title">{studentDisplayName(drawerRow)}</h2>
                                    <p className="feedef__drawer-subtitle">
                                        {classSectionLabel(drawerRow)}
                                        {drawerRow.roll_number ? ` · Roll No. ${drawerRow.roll_number}` : ''}
                                    </p>
                                </div>
                            </div>
                        </header>

                        <div className="feedef__drawer-body">
                            <section className="feedef__drawer-section">
                                <h4 className="feedef__drawer-section-title">Student</h4>
                                <div className="feedef__drawer-row">
                                    <span className="feedef__drawer-label">Father's Name</span>
                                    <span className="feedef__drawer-value">{drawerRow.father_name || '—'}</span>
                                </div>
                                <div className="feedef__drawer-row">
                                    <span className="feedef__drawer-label">Mother's Name</span>
                                    <span className="feedef__drawer-value">{drawerRow.mother_name || '—'}</span>
                                </div>
                                <div className="feedef__drawer-row">
                                    <span className="feedef__drawer-label">Address</span>
                                    <span className="feedef__drawer-value">{drawerRow.address || '—'}</span>
                                </div>
                                <div className="feedef__drawer-row">
                                    <span className="feedef__drawer-label">Contact</span>
                                    <span className="feedef__drawer-value">{drawerRow.father_contact || '—'}</span>
                                </div>
                                <div className="feedef__drawer-row">
                                    <span className="feedef__drawer-label">Total Overdue</span>
                                    <span className="feedef__drawer-value feedef__drawer-value--overdue">
                                        {formatCurrency(drawerRow.total_overdue_amount)}
                                    </span>
                                </div>
                            </section>

                            <section className="feedef__drawer-section">
                                <h4 className="feedef__drawer-section-title">Overdue Invoices</h4>
                                {drawerRow.overdue_invoices.length === 0 ? (
                                    <p className="feedef__details-empty">No overdue invoices on record.</p>
                                ) : (
                                    <table className="feedef__items-table">
                                        <thead>
                                            <tr>
                                                <th className="feedef__items-th">Invoice</th>
                                                <th className="feedef__items-th">Due Date</th>
                                                <th className="feedef__items-th">Status</th>
                                                <th className="feedef__items-th feedef__items-th--amount">Total</th>
                                                <th className="feedef__items-th feedef__items-th--amount">Paid</th>
                                                <th className="feedef__items-th feedef__items-th--amount">Balance</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {drawerRow.overdue_invoices.map((inv) => (
                                                <tr key={inv.id}>
                                                    <td className="feedef__items-td feedef__items-td--desc">
                                                        {inv.invoice_number}
                                                        {inv.title ? <span className="feedef__items-invoice-title"> · {inv.title}</span> : null}
                                                    </td>
                                                    <td className="feedef__items-td">{formatDate(inv.due_date)}</td>
                                                    <td className="feedef__items-td">
                                                        <span className={`feedef__status-badge feedef__status-badge--${inv.status}`}>
                                                            {STATUS_LABELS[inv.status] ?? inv.status}
                                                        </span>
                                                    </td>
                                                    <td className="feedef__items-td feedef__items-td--amount">{formatCurrency(parseFloat(inv.total_amount))}</td>
                                                    <td className="feedef__items-td feedef__items-td--amount">{formatCurrency(parseFloat(inv.paid_amount))}</td>
                                                    <td className="feedef__items-td feedef__items-td--amount feedef__items-td--due">{formatCurrency(parseFloat(inv.balance_amount))}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </section>
                        </div>
                    </aside>
                </>
            )}
        </div>
    );
}
